<template>
  <div class="wrapper w-100">
    <survey-question-text-block
      v-if="preview && !isNew"
      :question="value"
    ></survey-question-text-block>
    <div class="clickLayer text-right rounded" @click.stop="edit">
      <div
        v-if="preview && !isNew"
        class="mt-3 mr-2 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="edit"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit Question</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('delete')"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete Question</span>
        </v-tooltip>
      </div>
      <div
        class="mt-3 mr-3 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top v-if="questionIndex !== 0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', -1)"
            >
              <v-icon small>$sortUp</v-icon>
            </v-btn>
          </template>
          <span>Move Up</span>
        </v-tooltip>
        <v-tooltip top v-if="questionIndex !== page.questions.length - 1">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', 1)"
            >
              <v-icon small>$sortDown</v-icon>
            </v-btn>
          </template>
          <span>Move Down</span>
        </v-tooltip>
      </div>
    </div>

    <v-dialog
      v-model="openEditor"
      scrollable
      fullscreen
      hide-overlay
      persistent
      no-click-animation
      transition="dialog-bottom-transition"
    >
      <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
        <v-card rounded="0">
          <div class="elevation-3 primary">
            <v-container class="pa-0">
              <dialog-title
                :title="`${value.key === '' ? 'Add' : 'Edit'} Question`"
                class="white--text pl-md-0"
                @close="onCancel"
              >
                <template v-slot:actions="{ close }">
                  <v-btn dark icon @click="close">
                    <v-icon>$close</v-icon></v-btn
                  >
                </template>
              </dialog-title>
            </v-container>
          </div>
          <v-card-text ref="body">
            <v-container>
              <v-row>
                <v-col cols="12">
                  Fill out the form below to configure your text block.
                </v-col>
                <v-col cols="8">
                  <survey-editor-page-select
                    v-model="innerPageKey"
                    :pages="pages"
                    :locked="locked"
                  ></survey-editor-page-select>
                </v-col>

                <v-col cols="4">
                  <ValidationProvider
                    rules="required|min:2|max:500"
                    v-slot="{ errors }"
                    vid="Type"
                  >
                    <v-select
                      label="Type*"
                      v-model="innerValue.type"
                      :items="types"
                      outlined
                      :error-messages="errors"
                      disabled
                      rows="3"
                    ></v-select>
                  </ValidationProvider>
                </v-col>
                <v-col cols="2">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: questionKeys.filter((x) => x !== value.key),
                    }"
                    v-slot="{ errors }"
                    vid="key"
                  >
                    <v-text-field
                      label="Key*"
                      v-model="innerValue.key"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint="Cannot be changed once added."
                      :disabled="!isNew || locked"
                      :autofocus="isNew"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="10">
                  <ValidationProvider
                    rules="required|min:2|max:5000"
                    v-slot="{ errors }"
                    vid="Text"
                  >
                    <v-textarea
                      label="Text Block*"
                      v-model="innerValue.text"
                      outlined
                      :error-messages="errors"
                      rows="3"
                      :disabled="locked"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-btn icon color="secondary" @click="isAlert = !isAlert">
                      <v-icon>
                        {{ isAlert ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div>Show as Alert</div>
                  </div>
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="required ? 'secondary' : ''"
                      @click="isPrivate = !isPrivate"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ isPrivate ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div @click="isPrivate = !isPrivate" class="pointer">
                      Hide on Public Reporting
                    </div>
                  </div>
                </v-col>

                <v-col cols="4" v-if="isAlert">
                  <ValidationProvider
                    rules="required"
                    v-slot="{ errors }"
                    vid="AlertType"
                  >
                    <v-select
                      label="Alert Type*"
                      v-model="alertType"
                      :items="alertTypeOptions"
                      outlined
                      :error-messages="errors"
                    ></v-select>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-h6">Conditional Rules</div>
                  <p class="text-body-2">
                    Use the section below to create conditional rules for your
                    question.
                  </p>

                  <question-rules
                    v-for="rule in rules"
                    :key="rule.key"
                    :label="rule.label"
                    :ruleKey="rule.key"
                    :queryRules="queryRules"
                    :locked="locked"
                    v-model="innerValue.rules"
                    class="mb-4"
                  ></question-rules>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-4 py-0 elevation-3">
            <v-container>
              <div class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn text color="primary" @click="onCancel"> Cancel </v-btn>
                <v-btn v-if="!locked" type="submit" color="primary">
                  Ok
                </v-btn>
              </div>
            </v-container>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 0;

  .clickLayer {
    opacity: 0;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.15);
    transition: opacity 300ms ease-in-out;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }
}
</style>

<script>
import DialogTitle from "./DialogTitle.vue";
import SurveyQuestionTextBlock from "./Survey.Question.TextBlock.vue";
import {
  getQuestionSetting,
  questionTypeOptions,
  questionSettings,
  ruleTypes,
  alertTypeOptions,
} from "../constants/Survey";
import equal from "deep-is";
import SurveyEditorPageSelect from "./Survey.Editor.PageSelect.vue";
import QuestionRules from "./QuestionRules.vue";

const newOption = () => ({
  text: "",
  value: "",
  responseCountTarget: null,
  weight: null,
});

export default {
  name: "SurveyQuestionTextBlockEditor",
  components: {
    DialogTitle,
    SurveyQuestionTextBlock,
    SurveyEditorPageSelect,
    QuestionRules,
  },
  data: () => ({
    innerValue: {
      settings: [],
      options: [],
    },
    innerPageKey: "",
    openEditor: false,
    questionSettings,
    alertTypeOptions,
  }),
  props: {
    preview: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
    },
    questionIndex: {
      type: Number,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    pageKey: {
      type: String,
      required: true,
    },
    questionKeys: {
      type: Array,
      required: true,
    },
    questionMappingKeys: {
      type: Array,
      required: true,
    },
    queryRules: {
      type: Array,
      required: true,
    },
    locked: Boolean,
  },
  computed: {
    types() {
      return questionTypeOptions;
    },
    pageOptions() {
      return this.pages.map((x) => {
        return {
          text: x.displayName,
          value: x.key,
        };
      });
    },
    page() {
      return this.pages.find((x) => x.key === this.pageKey);
    },
    isNew() {
      return this.value.key === "";
    },
    rules() {
      let rules = [];

      if (!this.required)
        rules.push({
          label: "Show If",
          key: ruleTypes.showIf,
        });

      rules.push({
        label: "Hide If",
        key: ruleTypes.hideIf,
      });

      rules.push({
        label: "Stop If",
        key: ruleTypes.stopIf,
      });

      return rules;
    },
    isPrivate: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.private.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.private.key
        ).value = val + "";
      },
    },
    isAlert: {
      get() {
        if (!this.innerValue.settings) return false;
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.isAlert.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.isAlert.key
        ).value = val + "";
      },
    },
    alertColor: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.alertColor.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.alertColor.key
        ).value = val + "";
      },
    },
    alertType: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.alertType.key
          ).value
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.alertType.key
        ).value = val + "";
      },
    },
  },
  watch: {
    value(val) {
      if (!equal(val, this.innerValue)) this.applyDefaults();
    },
    pageKey(value) {
      if (value != this.innerPageKey)
        this.innerPageKey = JSON.parse(JSON.stringify(value));
    },
    openEditor(val) {
      if (val) {
        this.innerValue = JSON.parse(JSON.stringify(this.value));

        this.addingOption = false;
        this.addOption = newOption();
        this.editingOption = -1;
        this.editOption = newOption();
        this.deletingOption = -1;

        if (this.pageKey != this.innerPageKey)
          this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));

        this.optionGoals = this.hasOptionGoals ? true : false;

        if (this.$refs.body) {
          setTimeout(() => (this.$refs.body.scrollTop = 0), 1);
        }
      }
    },
  },
  methods: {
    edit() {
      if (this.$refs.observer) this.$refs.observer.reset();
      this.openEditor = true;
    },
    onSubmit() {
      this.$nextTick(() => {
        this.$refs.observer.validate().then((isValid) => {
          if (isValid) {
            if (this.innerValue.responseCountTarget === "") {
              this.innerValue.responseCountTarget = null;
            }

            this.$emit("input", this.innerValue);
            if (this.pageKey != this.innerPageKey) {
              this.$emit("set-page", this.innerPageKey);
            }
            this.openEditor = false;
          }
        });
      });
    },
    applyDefaults() {
      if (this.value) {
        let mirror = JSON.parse(JSON.stringify(this.value));
        let changed = false;

        let defaultSettings = [
          {
            key: questionSettings.isAlert.key,
            value: questionSettings.isAlert.default,
          },
          {
            key: questionSettings.alertType.key,
            value: questionSettings.alertType.default,
          },
          {
            key: questionSettings.private.key,
            value: questionSettings.private.default,
          },
        ];

        if (!mirror.settings) {
          mirror.settings = [];
          changed = true;
        }

        for (let i = 0; i < defaultSettings.length; i++) {
          if (!mirror.settings.some((x) => x.key === defaultSettings[i].key)) {
            mirror.settings.push(defaultSettings[i]);
            changed = true;
          }
        }

        if (changed) this.$emit("input", mirror);
        this.innerValue = JSON.parse(JSON.stringify(mirror));
      }
    },
    onCancel() {
      if (this.isNew) this.$emit("force-delete");
      this.openEditor = false;
    },
  },
  mounted() {
    if (this.pageKey)
      this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));
    this.applyDefaults();
  },
};
</script>
